// The Orders screen (spec/13 §5, F-POS-15): every sale at the venue as a list to scan — its time, its
// state in words, its total and anything still owed — filtered by state and searched by reference; a
// row opens the order in a centred dialog over the list. `orders:read` is needed to see it, and the server checks it
// again on every request. A kiosk order carries a "Kiosk" badge with its station and player, and a pass
// bought at the kiosk "pay at the desk" reads "Awaiting payment" until staff take it (F-KIO-14). `?sale=<sal_…>` opens
// that order's dialog — where a shift's cash timeline links an order number (F-OPS-04).

import type { Sale } from '@clavian/api-client';
import { formatOrderNumber } from '@clavian/domain/menu-choice';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, EmptyState, SegmentedControl, Table, type TableColumn } from '@clavian/ui-web';
import { Link, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import { PROBLEM, problemCode } from '../../api/problems';
import { LoadFailure } from '../../shell/LoadFailure';
import { NavIcon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { Forbidden, Page, SearchField, TableSkeleton, Toolbar } from '../../ui';
import { formatMoney } from '../sessions/money';
import { OrderDialog } from './OrderDialog';
import { useSales } from './queries';
import { useOrdersLive } from './realtime';
import { isAwaitingPayment, ORDER_STATUS_TONE, OrderSource, orderStatusKey, orderTone, useStationLabels } from './source';

const ORDERS_READ = 'orders:read';
const PLAYERS_READ = 'players:read';

export const SALE_STATUSES = ['placed', 'partially_paid', 'paid', 'partially_refunded', 'refunded', 'cancelled'] as const;
type SaleStatus = (typeof SALE_STATUSES)[number];

/** The list's filter: the states a counter thinks in. */
type Filter = 'all' | 'awaiting' | 'open' | 'paid' | 'refunds' | 'cancelled';

const FILTER_STATUSES: Readonly<Record<Exclude<Filter, 'all' | 'awaiting'>, ReadonlySet<SaleStatus>>> = {
  open: new Set(['placed', 'partially_paid']),
  paid: new Set(['paid']),
  refunds: new Set(['partially_refunded', 'refunded']),
  cancelled: new Set(['cancelled']),
};

/** Whether a sale falls under a filter: "Awaiting payment" is the kiosk orders waiting for the desk. */
function matches(filter: Filter, sale: Sale): boolean {
  if (filter === 'all') return true;
  if (filter === 'awaiting') return isAwaitingPayment(sale);
  return FILTER_STATUSES[filter].has(sale.status);
}

export function OrdersPage(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const access = useAccess({ venueId: venue.id });
  const canRead = access.status === 'ready' && access.can(ORDERS_READ);
  const canReadPlayers = access.status === 'ready' && access.can(PLAYERS_READ);
  const stationLabel = useStationLabels(venue.organizationId, venue.id);

  // Live from `venue:{v}:orders` while the socket is up (a kiosk order appears at once); polled otherwise.
  const live = useOrdersLive(venue.organizationId, venue.id, canRead);
  const sales = useSales(venue.organizationId, venue.id, canRead, live);
  const search = useSearch({ strict: false }) as { readonly sale?: unknown };
  const [openSaleId, setOpenSaleId] = useState<string | null>(typeof search.sale === 'string' && search.sale.startsWith('sal_') ? search.sale : null);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');

  const columns: readonly TableColumn<Sale>[] = [
    {
      key: 'order',
      header: t('orders.column.id'),
      cell: (sale) => (
        <button type="button" className="console-cell-link" onClick={() => setOpenSaleId(sale.id)}>
          <span className="console-code">{orderRef(sale)}</span>
          <OrderSource organizationId={venue.organizationId} sale={sale} stationLabel={stationLabel} canReadPlayers={canReadPlayers} />
        </button>
      ),
    },
    { key: 'placed', header: t('orders.column.placed'), cell: (sale) => <span className="console-num">{formatClock(sale.placedAt)}</span> },
    {
      key: 'items',
      header: t('orders.column.items'),
      cell: (sale) => <span className="console-muted">{sale.lines.map((line) => `${line.quantity}× ${line.name}`).join(', ')}</span>,
    },
    {
      key: 'status',
      header: t('orders.column.status'),
      cell: (sale) => (
        <Badge tone={ORDER_STATUS_TONE[orderStatusKey(sale)]} dot>
          {t(`orders.status.${orderStatusKey(sale)}`)}
        </Badge>
      ),
    },
    { key: 'total', header: t('orders.column.total'), numeric: true, cell: (sale) => <span className="console-money">{formatMoney(sale.total)}</span> },
    {
      key: 'outstanding',
      header: t('orders.column.outstanding'),
      numeric: true,
      cell: (sale) =>
        sale.outstanding.amount > 0 ? (
          <span className="console-money console-orders__owed">
            {/* On a phone the row is a card with no column headers, so the owed figure says what it is. */}
            <span className="console-orders__owed-label">{t('orders.column.outstanding')} </span>
            {formatMoney(sale.outstanding)}
          </span>
        ) : (
          <span className="console-subtle">—</span>
        ),
    },
  ];

  let body: ReactNode;
  if (!canRead && access.status === 'ready') {
    body = <Forbidden message={t('orders.notPermitted')} />;
  } else if (sales.data === undefined) {
    if (sales.isError && problemCode(sales.error) === PROBLEM.forbidden) {
      body = <Forbidden message={t('orders.notPermitted')} />;
    } else {
      body = sales.isError ? <LoadFailure error={sales.error} message={t('orders.unavailable')} onRetry={() => void sales.refetch()} /> : <TableSkeleton />;
    }
  } else if (sales.data.length === 0) {
    body = <EmptyState icon={<NavIcon name="orders" />} title={t('orders.emptyTitle')} description={t('orders.empty')} action={<Link className="clav-button clav-button--primary" to="/v/$venueSlug/sell" params={{ venueSlug: venue.slug }}>{t('orders.emptyAction')}</Link>} />;
  } else {
    const all = sales.data;
    const count = (key: Exclude<Filter, 'all'>) => all.filter((sale) => matches(key, sale)).length;
    const needle = query.trim().toLowerCase();
    const rows = all
      .filter((sale) => matches(filter, sale))
      .filter(
        (sale) =>
          needle === '' ||
          sale.id.toLowerCase().includes(needle) ||
          (formatOrderNumber(sale.orderNumber) ?? '').includes(needle) ||
          sale.lines.some((line) => line.name.toLowerCase().includes(needle)),
      );
    body = (
      <>
        <Toolbar>
          <SearchField label={t('orders.search')} value={query} onChange={(event) => setQuery(event.target.value)} />
          <SegmentedControl<Filter>
            label={t('orders.filter.status')}
            value={filter}
            onValueChange={setFilter}
            options={[
              { value: 'all', label: t('orders.filter.all'), count: all.length },
              ...(count('awaiting') > 0 || filter === 'awaiting' ? [{ value: 'awaiting' as const, label: t('orders.filter.awaiting'), count: count('awaiting') }] : []),
              { value: 'open', label: t('orders.filter.open'), count: count('open') },
              { value: 'paid', label: t('orders.filter.paid'), count: count('paid') },
              { value: 'refunds', label: t('orders.filter.refunds'), count: count('refunds') },
              { value: 'cancelled', label: t('orders.filter.cancelled'), count: count('cancelled') },
            ]}
          />
        </Toolbar>
        <div className="console-table-card">
          <Table
            className="console-orders-table"
            caption={t('orders.caption')}
            captionHidden
            columns={columns}
            rows={rows}
            rowKey={(sale) => sale.id}
            rowTone={orderTone}
            empty={t('orders.filterEmpty')}
          />
        </div>
      </>
    );
  }

  return (
    <Page title={t('orders.title')} description={t('orders.intro')}>
      {body}
      <OrderDialog organizationId={venue.organizationId} venueId={venue.id} saleId={openSaleId} can={access.can} onClose={() => setOpenSaleId(null)} />
    </Page>
  );
}

/**
 * How the list names an order: its number for the business day, "#0421" (F-FNB-04) — what the counter calls
 * out and the kitchen ticket shows — or, until it is allocated, its shortened id.
 */
export function orderRef(sale: Pick<Sale, 'id' | 'orderNumber'>): string {
  return formatOrderNumber(sale.orderNumber) ?? shortId(sale.id);
}

/** A TypeID shortened for the list: the prefix and the last six characters, e.g. `sal_…a1b2c3`. */
function shortId(id: string): string {
  const [prefix, body] = id.split('_', 2);
  if (body === undefined) return id;
  return `${prefix}_…${body.slice(-6)}`;
}

/** The local clock date and time of an RFC 3339 instant. */
function formatClock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
}
